import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '@lbmx/phoenix-lib-core';
import { SpinnerService, ToastrNotificationService } from '@lbmx/phoenix-lib-utils';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { GroupToken, IConfigCountry, IConfigProvince, IDropDown, INewSupplierProfileForm } from 'src/app/dtos/group/group.dto';
import { GroupService } from 'src/app/services/group/group.service';

@Component({
  selector: 'app-new-supplier',
  templateUrl: './new-supplier.component.html',
  styleUrls: ['./new-supplier.component.scss']
})
export class NewSupplierComponent implements OnInit, OnDestroy {

  public validToken = false;
  public subscriptions: Subscription[] = [];
  public countriesDropDown: IDropDown[];
  public provincesDropDown: IDropDown[];
  public provinces: IConfigProvince[];

  public newSupplierProfileForm: FormGroup = this.formBuilder.group(
    {
      companyName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      city: ['', [Validators.required]],
      country: ['', [Validators.required]],
      province: ['', [Validators.required]],
      postalCode: ['', [Validators.required]],
      mainPhone: ['', [Validators.required]],
      financialInstitution: ['', [Validators.required]],
      bankingAddress: ['', [Validators.required]],
      bankingCity: ['', [Validators.required]],
      bankingProvince: ['', [Validators.required]],
      bankingPostalCode: ['', [Validators.required]],
      bankingPhone: ['', [Validators.required]],
      routingNumber: ['', [Validators.required]],
      accountNumber: ['', [Validators.required]],
      accountingName: ['', [Validators.required]],
      accountingPhone: ['', [Validators.required]],
      accountingEmail: ['', [Validators.required, Validators.email]],
    },
  );

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private loggerService: LoggerService,
    private toastSrv: ToastrNotificationService,
    private spinnerService: SpinnerService,
    private groupService: GroupService,
    private translocoService: TranslocoService,
  ) { }

  public ngOnInit(): void {
    this.spinnerService.on();
    this.validateToken();
    this.getCountries();
    this.getProvinces();
  }

  public validateToken(): void {
    this.subscriptions.push(
      this.groupService
        .validateGroupLink(this.buildValidateTokenRequest())
        .pipe(take(1))
        .subscribe(
          (res) => {
            if (res?.status === 'Ok') {
              this.loggerService.info(
                'groupTokenResponse',
                'GroupService.validateGroupLink',
                JSON.stringify(res)
              );
              this.validToken = true;
            }
          },
          (err) => {
            this.loggerService.error(
              'Error group token',
              'GroupService.validateGroupLink',
              err
            );
            this.validToken = false;
          }
        )
        .add(() => {
          this.spinnerService.off();
        })
    );
  }

  public getCountries() {
    this.groupService.getCountries()
      .pipe()
      .subscribe((val) => {
        this.countriesDropDown = val.map(
          (country: IConfigCountry): IDropDown => {
            return {
              label: country.countryName,
              value: country.countryCode.trim(),
            };
          }
        );
      });
  }

  public getProvinces() {
    this.groupService.getProvinces()
      .pipe()
      .subscribe((val) => {
        this.provinces = val;
      });
  }

  public onCountryDropDownSelect() {
    const filteredProv = this.provinces.filter(prov => prov.countryCode === this.newSupplierProfileForm.get('country').value);
    this.provincesDropDown = filteredProv.map((province: IConfigProvince): IDropDown => {
      return {
        label: province.provinceName,
        value: province.provinceCode.trim(),
      };
    });
  }

  public ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
    this.spinnerService.off();
  }

  public buildAddRequest(): INewSupplierProfileForm {
    return {
      token: this.route.snapshot.queryParamMap.get('token'),
      companyName: this.newSupplierProfileForm.value.companyName,
      address: this.newSupplierProfileForm.value.address,
      city: this.newSupplierProfileForm.value.city,
      country: this.newSupplierProfileForm.value.country,
      province: this.newSupplierProfileForm.value.province,
      postalCode: this.newSupplierProfileForm.value.postalCode,
      mainPhone: this.newSupplierProfileForm.value.mainPhone,
      financialInstitution: this.newSupplierProfileForm.value.financialInstitution,
      bankingAddress: this.newSupplierProfileForm.value.bankingAddress,
      bankingCity: this.newSupplierProfileForm.value.bankingCity,
      bankingProvince: this.newSupplierProfileForm.value.bankingProvince,
      bankingPostalCode: this.newSupplierProfileForm.value.bankingPostalCode,
      bankingPhone: this.newSupplierProfileForm.value.bankingPhone,
      routingNumber: this.newSupplierProfileForm.value.routingNumber,
      accountNumber: this.newSupplierProfileForm.value.accountNumber,
      accountingName: this.newSupplierProfileForm.value.accountingName,
      accountingPhone: this.newSupplierProfileForm.value.accountingPhone,
      accountingEmail: this.newSupplierProfileForm.value.accountingEmail,
    };
  }

  private buildValidateTokenRequest(): GroupToken {
    const request: GroupToken = new GroupToken();
    request.token = this.route.snapshot.queryParamMap.get('token');
    return request;
  }

  public save() {
    this.addNewSupplier();
  }

  public addNewSupplier() {
    this.spinnerService.on();
    this.subscriptions.push(
      this.groupService
        .submitNewSupplier(this.buildAddRequest())
        .pipe(finalize(() => this.spinnerService.off()))
        .subscribe(
          (response) => {
            this.loggerService.info(
              'AddSupplierResponse',
              'AddSupplierService.addSupplier',
              JSON.stringify(response)
            );
            if (response.wasAdded === true) {
              this.toastSrv.successNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_SUPPLIER_SUCCESS'
                ),
                null,
                5000
              );
            }
          },
          (error) => {
            if (error.error != null && error.error.errorCode === 3) {
              this.toastSrv.warnNotify(
                this.translocoService.translate(
                  'ONBOARDING.SUPPLIER_ALREADY_EXIST'
                ),
                null,
                5000
              );
            } else {
              this.toastSrv.errorNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_SUPPLIER_FAILURE'
                ),
                null,
                5000
              );
            }
            this.loggerService.error(
              'AddSupplierResponse',
              'AddSupplierService.addSupplier',
              JSON.stringify(error)
            );
          }
        )
        .add(() => {
          this.newSupplierProfileForm.reset();
        })
    );
  }
}
